/**
 * Braze Web SDK initialization.
 * In-app messages are disabled when the site is viewed inside the native app's webview
 * (the native app already uses Braze for in-app messaging).
 *
 * @see https://www.braze.com/docs/developer_guide/sdk_integration?sdktab=web
 */

// eslint-disable-next-line import/no-unresolved -- @braze/web-sdk from package.json
import * as braze from '@braze/web-sdk';

import { BRAZE_WEB_API_KEY_DEV, BRAZE_WEB_API_KEY_PROD } from 'constants/publicKeys';
import { getIsProduction } from 'utils';

const BRAZE_WEB_SDK_ENDPOINT = 'sdk.iad-05.braze.com';

let brazeSessionActive = false;

/**
 * Initializes the Braze Web SDK for in-app messages and analytics.
 * No-op when running in native webview or when API key is missing.
 */
export function initializeBraze() {
    if (window.isNativeApp) {
        return;
    }

    const isProd = getIsProduction();
    const apiKey = isProd ? BRAZE_WEB_API_KEY_PROD : BRAZE_WEB_API_KEY_DEV;

    if (!apiKey) {
        return;
    }

    try {
        braze.enableSDK();
        braze.initialize(apiKey, {
            baseUrl: BRAZE_WEB_SDK_ENDPOINT,
            enableLogging: false,
            allowUserSuppliedJavascript: true,
        });
        braze.automaticallyShowInAppMessages();

        if (typeof window !== 'undefined') {
            window.braze = braze;
        }
        brazeSessionActive = true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Braze Web SDK initialization failed:', e);
    }
}

/**
 * Ends the Braze session on sign-out: wipes local user data and disables the SDK
 * so logged-out visitors do not receive in-app messages or count toward MAU.
 * Does not start an anonymous session.
 */
export function clearBrazeSession() {
    if (window.isNativeApp || !brazeSessionActive) {
        return;
    }

    try {
        braze.wipeData();
        braze.destroy();
        braze.disableSDK();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Braze Web SDK session cleanup failed:', e);
    } finally {
        brazeSessionActive = false;

        if (typeof window !== 'undefined') {
            delete window.braze;
        }
    }
}
